import type { TaskDistanceView } from '../../api/task-distances';
import {
  LOWER_BOUND_NOTE,
  RUNNING_SUFFIX,
  cleaningTimeCell,
  distanceCell,
  type DistanceCell,
} from './task-distance';

/** The figure as a drawer writes it: a total still growing says so --
 *  a lower bound too ("≥ 1,235 m so far"), or it reads as a final one. */
function figure(cell: DistanceCell): string {
  return cell.growing ? `${cell.text} ${RUNNING_SUFFIX}` : cell.text;
}

/**
 * Distance and Cleaning time as two rows of a task drawer, the live one
 * and the older one alike.
 *
 * THE TABLE'S OWN CELLS (`distanceCell`, `cleaningTimeCell`), from the
 * values its columns read -- the live store for a live task, the
 * completion record's copy for an older one -- so a drawer can never
 * show a different number from the row it was opened from: a dash when
 * nothing was measured (never `0 m`), whole metres, `≥` in front of a
 * lower bound, head-down time for Cleaning time.
 *
 * WHAT THE TABLE SAYS ON HOVER, THE DRAWER SAYS IN WORDS. A drawer is
 * read on a phone as often as not, and a phone has no hover: a total
 * still growing reads "so far" after it, and a lower bound carries its
 * note under the two rows. The hover note stays too, for a mouse.
 */
export function TaskDistanceRows({
  view,
  running = false,
}: {
  view: TaskDistanceView | null | undefined;
  /** The task's totals are still growing (`distanceStillGrowing`). */
  running?: boolean;
}) {
  const distance = distanceCell(view, running);
  const cleaning = cleaningTimeCell(view, running);
  const lowerBound = distance.lowerBound || cleaning.lowerBound;
  return (
    <>
      <div className="nx-task-drawer__row">
        <span>Distance</span>
        <span title={distance.title} data-testid="task-drawer-distance">
          {figure(distance)}
        </span>
      </div>
      <div className="nx-task-drawer__row">
        <span>Cleaning time</span>
        <span title={cleaning.title} data-testid="task-drawer-cleaning-time">
          {figure(cleaning)}
        </span>
      </div>
      {lowerBound && (
        <p className="nx-task-drawer__sub" data-testid="task-drawer-distance-note">
          {LOWER_BOUND_NOTE}
        </p>
      )}
    </>
  );
}
